import { create, fromBinary, toBinary } from '@bufbuild/protobuf';
import { v4 as uuidv4 } from 'uuid';

import { logger } from '@gem/modules/logger';
import {
  WsRequest as WsRequestBuilder,
  WsResponse as WsResponseBuilder,
} from '@gem/modules/wsClient/wsManagers';
import {
  WsRequest,
  type IWsSpecificNetworkService,
  type ResponseResult,
} from '@gem/modules/wsClient/wsManagers';
import {
  type Listener,
  Stream,
  NetworkEventType,
  WsNetworkService,
} from '@gem/modules/wsClient/wsServices';

import { UnauthorizedWsCallsApiError, WsAppErrorCode } from './UnauthorizedWsCallsApiError';
import { RealtimeCallEvent } from './types/call/call_realtime_events_pb';
import {
  CallRequest,
  CallResponse,
  CallWsRequest,
  CallWsRequestSchema,
  CallWsResponse,
  CallWsResponseSchema,
  CallPingRequestSchema,
  CallPongResponse,
} from './types/call_api_pb';

const getUniqueRequestId = ((): (() => number) => {
  let currentId = 0;

  return (): number => {
    currentId += 1;
    return currentId;
  };
})();

export class UnauthorizedWsCallsApiNetworkService
  extends WsNetworkService
  implements IWsSpecificNetworkService
{
  private extractedMessages = new Stream<CallWsResponse>();
  private wsLogMessages = new Stream<{ id: string; message: CallWsResponse | CallWsRequest }>();

  constructor() {
    super();
    this.on(NetworkEventType.Message, (message: MessageEvent) => this.extractWsMessage(message));
  }

  public extractWsMessage(event: MessageEvent): void {
    const parsedMessage = fromBinary(CallWsResponseSchema, new Uint8Array(event.data));

    this.extractedMessages.emit(parsedMessage);
    this.logWsResponse(parsedMessage);
  }

  public sendRequest(
    requestData: CallRequest['type'],
    request: WsRequest,
    listener: Listener<ResponseResult<UnauthorizedWsCallsApiError>>
  ): void {
    const requestId = BigInt(getUniqueRequestId());

    const message = create(CallWsRequestSchema, {
      type: {
        case: 'request',
        value: {
          requestId,
          type: requestData,
        },
      },
    });

    this.logWsRequest(message);

    const binaryMessage = toBinary(CallWsRequestSchema, message);

    const unsubscribe = this.extractedMessages.on((message: CallWsResponse) => {
      if (message.type.case === 'response' && message.type.value.requestId === requestId) {
        unsubscribe();

        const result = this.handleResponse(message.type.value.type);
        if (result.retry) {
          //TODO: need to figure out how to implement retry mechanism
          setTimeout(() => {
            listener(result);
          }, 2000);
          return;
        }
        listener(result);
      }
    });

    const result = this.ws.send(binaryMessage);

    if (result) {
      request.isSent = true;
    }
  }

  onEvent(eventName: string, listener: Listener<RealtimeCallEvent['event']['value']>): () => void {
    return this.extractedMessages.on((message: CallWsResponse) => {
      if (message.type.case === 'event' && message.type.value?.event.case === eventName) {
        listener(message.type.value.event.value);
      }
    });
  }

  public onWsLog(
    listener: Listener<{ id: string; message: CallWsResponse | CallWsRequest }>
  ): () => void {
    return this.wsLogMessages.on(listener);
  }

  private handleResponse(data: CallResponse['type']): ResponseResult {
    //TODO: this code presents as example on how to handle common errors
    if (data.case === 'error') {
      const errorData = data.value.type;
      if (errorData.case === 'internalError') {
        const error = new UnauthorizedWsCallsApiError(WsAppErrorCode.BadData, 'bad data error');
        return {
          response: null,
          error,
          retry: false,
        };
      }
      //TODO: random error for checking retry mechanism
      if (errorData.case === 'resourceExhaustedError') {
        return { response: null, error: null, retry: true };
      }
    }
    return { response: data.value, error: null, retry: false };
  }

  private logWsResponse(message: CallWsResponse): void {
    const id = message.type.case === 'event' ? uuidv4() : message.type.value?.requestId.toString();
    if (!id) {
      logger.log('We got response which does not have requestID:', message);
      return;
    }
    this.wsLogMessages.emit({ id, message });
  }

  private logWsRequest(message: CallWsRequest): void {
    const id = message.type.value?.requestId.toString();
    if (!id) {
      logger.log('We send request which does not have requestID:', message);
      return;
    }

    this.wsLogMessages.emit({ id, message });
  }

  public async pingPongRequest(): Promise<void> {
    const request = new WsRequestBuilder({
      case: 'ping',
      value: create(CallPingRequestSchema, {}),
    });
    const response = new WsResponseBuilder<CallPongResponse>(request);

    this.sendRequest(request.message as CallRequest['type'], request, (responseResult) => {
      if (responseResult.response) {
        response.resolve(responseResult.response);
      }
    });

    await response.promise;
  }
}
